import { t } from '@lingui/macro'
import { useState } from 'react'
import styled from 'styled-components'

import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'

type SingleSelectOption = {
  label: string
  value: string
} & Record<string, unknown>

type SingleSelectProps = {
  options: SingleSelectOption[]
  title?: string
  description?: string
  onNext: (option: SingleSelectOption) => void
  isLoading: boolean
}

export const SingleSelect = ({ options, title, description, onNext, isLoading }: SingleSelectProps) => {
  const [selected, setSelected] = useState<SingleSelectOption | undefined>()

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        {title && <H3 noMargin>{title}</H3>}
        {description && <Markdown content={description} typography='BodyMediumRegular' />}
        <StSingleSelectContainer>
          {options?.map((option) => (
            <StSingleSelectOption
              key={option.value}
              selected={selected?.value === option.value}
              onClick={() => setSelected(option)}
            >
              {option.label}
            </StSingleSelectOption>
          ))}
        </StSingleSelectContainer>
      </BookingFlowCenteredContent>

      <BookingFlowActions>
        <Button
          variant='Primary'
          type='submit'
          disabled={!selected || isLoading}
          isLoading={isLoading}
          onClick={() => selected && onNext(selected)}
        >
          {t({
            id: 'globals.continue',
            message: 'Ga verder',
          })}
        </Button>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}

const StSingleSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
`

const StSingleSelectOption = styled.div<{ selected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  border: 1px solid ${({ theme }) => theme.Components.Conversational.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;

  transition-property: border, background, color;
  transition-duration: 175ms;
  transition: all 175ms ease-out;

  border-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveBorder : theme.Components.Conversational.Border};
  background-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveFill : theme.Components.Conversational.Fill};
  color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveText : theme.Components.Conversational.Text};
`
